/**
 * pages/Pembelian.js
 * Purchase transactions (Phase T1c, PRD §5.2). Same skeleton as Penjualan — a day navigator
 * frames the view, table shows one day's PEMBELIAN + TRANSFER transactions at a time (transfer
 * support added in batch 2, see that entry below). Differences from Penjualan: No. Invoice shows
 * `supplierInvoiceNo` for an ordinary PEMBELIAN row (dark gray, free text — never minted; a
 * TRANSFER row shows its real minted `invoiceNumber` instead, same blue mono as Penjualan's own);
 * no salesperson COLUMN (Detail/Edit still show Sales for a transfer row — see batch 2, below);
 * an ordinary PEMBELIAN's own stock sign is positive/green (PEMBELIAN adds to the anchor bucket);
 * day cards read Total Nilai Pembelian / Utang Hari Ini.
 *
 * Post-launch fix (Fix 3, multi-branch cabang feature): gained the same branch-awareness
 * Penjualan.js already has — a `branches` prop, a second-level branch toggle (Cabang-tab-only,
 * on the SAME row as the primary toggle, far right), and LOKASI badge branch-name resolution.
 * No filtering flow was needed for the transaction form's own branch field (unlike Penjualan's
 * Sales picker) since PEMBELIAN never has a salesperson to filter against — see
 * `TransactionFormModal.js`'s `needBranchPicker`. No new branch-management UI here — branches
 * are a shared, global list managed from Penjualan's existing "Kelola Cabang" button.
 *
 * **Pembelian batch 2 (transfer support): TRANSFER now shares this page's list with PEMBELIAN**
 * — the base filter below is `type === "PEMBELIAN" || type === "TRANSFER"`, mirroring
 * Penjualan.js's own PENJUALAN+TRANSFER list exactly. Transfer is a real, two-sided fact
 * (`utils/piutangUtang.js`'s `transferStatusFor` already exists precisely because a transfer is
 * simultaneously true from BOTH sides' own books) — Penjualan shows it as a sale by the SOURCE
 * branch, this page now shows the exact same row as a purchase by the DESTINATION branch. This
 * is a genuine, deliberate PERSPECTIVE FLIP, not a superficial filter change, and it touches
 * more than the base filter:
 *   - **Lokasi/Penjual columns are perspective-flipped from Penjualan's own Lokasi/Pembeli.**
 *     A transfer's `location`/`branchId` always encode the SOURCE (unchanged, page-independent
 *     fact) — Penjualan's Lokasi reads them directly; this page's Lokasi instead shows the
 *     DESTINATION (`transferKlienName`, reused as-is from Piutang & Utang's own identical need —
 *     the "real debtor" IS the destination, exactly what "home" means here), and Penjual shows
 *     the SOURCE (`resolveLocationLabel(t.location, t.branchId, branches)` — the same formula
 *     Penjualan's own Lokasi badge already used inline, now named so this new use can share it).
 *   - **The Pusat/Cabang tab filter tests the DESTINATION bucket for a TRANSFER row, not
 *     `t.location`** (`homeLocationOf`, below) — Penjualan's own identical-looking filter is
 *     correct testing `t.location` directly ONLY because Penjualan's own perspective IS the
 *     source; naively copying that filter here would file a Pusat→MMC transfer under this
 *     page's "Pusat" tab, when MMC (the buyer) is who this page's "Cabang" tab means. The
 *     second-level branch sub-filter needs NO equivalent flip — `t.branchId` already names the
 *     one Cabang branch a transfer ever touches, source or destination, unambiguously.
 *   - **`salespeople`/`onCreateSalespersonRole` are now real, threaded-through props** (were
 *     `salespeople={[]}` hardcoded at every modal call site, and `onCreateSalespersonRole`
 *     wasn't even accepted) — invariant 12 requires a real salesperson on every TRANSFER, and an
 *     empty array meant Edit/Detail could never resolve or display one for a transfer opened
 *     from here, even though the role genuinely exists (App.js now passes the real
 *     `data.salespeople`/`createSalespersonRole` down, same as Penjualan.js already did).
 *   - **`TotalSetelahBiayaCell`/`TransactionDetailModal`/`BayarTagihanModal` all take an
 *     explicit `perspective="PEMBELIAN"` prop** now — see each component's own header comment;
 *     without it, a transfer's Pihak/wording would still describe the SOURCE side even when
 *     opened from here.
 *   - **`TransactionDetailModal` also now receives `persons`** — this page already accepted it
 *     (for its own Penjual `ContactPicker`) but never threaded it through to that modal;
 *     `resolveSalespersonName`/`resolveSalespersonCode` both take `persons` first, so without
 *     it a transfer's real Sales name/code could never resolve there either.
 *   - **Still zero print buttons, on every row, transfer included** — Pembelian prints nothing,
 *     full stop; this page still never passes a single `onPrint*` callback to `TxnRowActions`.
 *   - **CLOSED by Piutang & Utang batch 3**: the StatusPill inconsistency named above (a Belum
 *     Lunas transfer reading green "Piutang" here despite this page's own "Utang Hari Ini" day
 *     card already counting it as debt) is fixed — this page's own row render now threads the
 *     DESTINATION's own branchId into `txnStatus(t, perspectiveBranchId)` (see that call site's
 *     own comment), so a Belum Lunas transfer's StatusPill reads "Utang" here, matching the day
 *     card.
 *
 * **Section F, batch 4 — "Sembunyikan transfer internal"**: same toolbar checkbox as
 * Penjualan.js (default OFF — internal shown), spliced BEFORE `dayTxns` so the day cards
 * (Total Nilai Pembelian / Utang Hari Ini) are scoped by it too — see Penjualan.js's own header
 * comment for the full reasoning; identical here, just Pembelian's own card labels.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fmtQty, fmtDateShort, today } from "../utils/idGenerators";
import { bucketLabel } from "../utils/transformations";
import { txnTotal, txnSisa, txnStatus, dueState } from "../utils/txnStatus";
import { resolveLocationLabel } from "../utils/branches";
import { transferKlienName } from "../utils/piutangUtang";
import TransactionFormModal from "../components/TransactionFormModal";
import TransactionDetailModal from "../components/TransactionDetailModal";
import BayarTagihanModal from "../components/BayarTagihanModal";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { LinkedExpensesDeleteNote } from "../components/ExpensesContext";
import DayNav from "../components/DayNav";
import TxnRowActions from "../components/TxnRowActions";
import TxnNilaiCell from "../components/TxnNilaiCell";
import { SubtotalBarangCell, RincianTambahanCell, TotalSetelahBiayaCell } from "../components/TxnCostCells";
import TxnBarangStokCell from "../components/TxnBarangStokCell";
import { StatusPill, DueBadge } from "../components/TxnBadges";

// Single source of truth for the Stok sub-column's width (dashboard alignment fix) — see
// Penjualan.js's own identical constant for the full reasoning (feeds both the outer
// <colgroup>'s Stok <col> and TxnBarangStokCell's `stokWidth` prop, so the header's Stok width
// and the merged body cell's internal split can never independently drift).
const STOK_COL_WIDTH = 110;

// Pembelian batch 2 (transfer support) — see this file's own header comment for the full
// reasoning. This page's own "home" bucket for the Pusat/Cabang tab filter: a TRANSFER's
// destination (the buyer, from THIS page's perspective), never its stored `location` (always
// the source) — an ordinary PEMBELIAN's own `location` already IS its home, unchanged.
function homeLocationOf(t) {
  return t.type === "TRANSFER" ? (t.location === "PUSAT" ? "CABANG" : "PUSAT") : t.location;
}

export default function Pembelian({
  transactions = [], items = [], contacts = [], persons = [], branches = [], siblingLinks = [], salespeople = [],
  onCreateTransaction, onUpdateTransaction, onDeleteTransaction,
  onRecordPayment, onCreateSalespersonRole,
  onCreateContact, onCreateContactFull, onOpenItem, defaultDueDays,
}) {
  const [selectedDate, setSelectedDate] = useState(() => today());
  const [q, setQ] = useState("");
  const [searchBy, setSearchBy] = useState("semua"); // semua | invoice | klien | lokasi | barang
  const [locFilter, setLocFilter] = useState("SEMUA"); // SEMUA | PUSAT | CABANG
  const [branchFilter, setBranchFilter] = useState("SEMUA"); // SEMUA | a branch id (Cabang tab only)
  // Hide-internal filter (section F, batch 4) — see Penjualan.js's own identical state for the
  // full reasoning; default OFF (internal shown).
  const [hideInternal, setHideInternal] = useState(false);
  const [formModal, setFormModal] = useState(null); // null | { mode, type, initial }
  // Phase T3: held as an ID, re-derived live from `transactions` below — see Penjualan.js's
  // own identical comment for why (a correction made inside the modal must be visible without
  // closing it).
  const [detailTxnId, setDetailTxnId] = useState(null);
  const [bayarTagihanTxnId, setBayarTagihanTxnId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const byCode = useMemo(() => new Map(items.map((it) => [it.internalCode, it])), [items]);
  const contactsById = useMemo(() => new Map(contacts.map((c) => [c.id, c])), [contacts]);
  // Pembelian batch 2 — the Penjual column + its own "Penjual" search scope, mirroring
  // Penjualan.js's own `klienDisplay`: a TRANSFER row's real counterparty here is the SOURCE
  // side (never a contact — see this file's own header comment), an ordinary PEMBELIAN reads
  // its real contact same as always. Never read a TRANSFER row's own `contactId` here at all —
  // it resolves to an app-maintained internal stand-in whose `.name` is a fixed, never-real
  // placeholder ("__internal__").
  const penjualDisplay = useCallback((t) => {
    if (t.type === "TRANSFER") return resolveLocationLabel(t.location, t.branchId, branches);
    const c = contactsById.get(t.contactId);
    return c ? c.name : "";
  }, [branches, contactsById]);
  // Search gap fix: the Lokasi column's own display formula, named so the "Lokasi" search option
  // and the table cell can never disagree — same expression the JSX below renders. Perspective-
  // flipped for a TRANSFER (destination = home here, see this file's own header comment), unlike
  // Penjualan.js's own `lokasiDisplay`, which never needs the flip.
  const lokasiDisplay = useCallback((t) => (
    t.type === "TRANSFER" ? transferKlienName(t, branches) : resolveLocationLabel(t.location, t.branchId, branches)
  ), [branches]);
  const detailTxn = detailTxnId ? transactions.find((t) => t.id === detailTxnId) || null : null;
  const bayarTagihanTxn = bayarTagihanTxnId ? transactions.find((t) => t.id === bayarTagihanTxnId) || null : null;

  // Pembelian batch 2: PEMBELIAN + TRANSFER together — mirrors Penjualan.js's own
  // PENJUALAN+TRANSFER list exactly (see this file's own header comment for the full story).
  const listTxns = useMemo(() => transactions.filter((t) => t.type === "PEMBELIAN" || t.type === "TRANSFER"), [transactions]);
  // Location filter (PRD §7.1) — tests `homeLocationOf(t)`, NOT `t.location` directly: a
  // TRANSFER's own `location` is always the SOURCE bucket, but this page's own tabs mean "who's
  // buying" (the destination) — see `homeLocationOf`'s own comment above.
  const locScoped = useMemo(
    () => (locFilter === "SEMUA" ? listTxns : listTxns.filter((t) => homeLocationOf(t) === locFilter)),
    [listTxns, locFilter]
  );
  // Post-launch fix (Fix 3): second-level branch filter, meaningful only while
  // locFilter === "CABANG" — same splice point/guard as Penjualan.js's own branchScoped memo.
  const branchScoped = useMemo(
    () => (locFilter !== "CABANG" || branchFilter === "SEMUA")
      ? locScoped
      : locScoped.filter((t) => t.branchId === branchFilter),
    [locScoped, locFilter, branchFilter]
  );
  // Hide-internal filter (section F, batch 4) — see Penjualan.js's own identical memo for the
  // full reasoning (spliced before `dayTxns` so "Total Nilai Pembelian"/"Utang Hari Ini" follow
  // the toggle too, not just the rendered table).
  const internalScoped = useMemo(
    () => (hideInternal ? branchScoped.filter((t) => t.type !== "TRANSFER") : branchScoped),
    [branchScoped, hideInternal]
  );
  const dayTxns = useMemo(() => internalScoped.filter((t) => t.date === selectedDate), [internalScoped, selectedDate]);

  // Leaving the Cabang tab resets the branch filter — without this, a previously-picked
  // branch would silently reapply next time the user returns to Cabang. (Unlike Penjualan.js,
  // Pembelian has no "Sales" search-scope option to reset alongside it — Pembelian never has a
  // salesperson.)
  useEffect(() => {
    if (locFilter !== "CABANG") setBranchFilter("SEMUA");
  }, [locFilter]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = dayTxns;
    if (needle) {
      list = list.filter((t) => {
        // A TRANSFER never carries supplierInvoiceNo (it mints a real invoiceNumber instead,
        // same as Penjualan) — exactly one of the two is ever populated per row, so a plain OR
        // fallback is safe.
        const invoiceHit = (t.supplierInvoiceNo || t.invoiceNumber || "").toLowerCase().includes(needle);
        const klienHit = penjualDisplay(t).toLowerCase().includes(needle);
        const lokasiHit = lokasiDisplay(t).toLowerCase().includes(needle);
        const barangHit = (t.lines || []).some((l) =>
          (l.name || "").toLowerCase().includes(needle) || (l.internalCode || "").toLowerCase().includes(needle));
        if (searchBy === "invoice") return invoiceHit;
        if (searchBy === "klien") return klienHit;
        if (searchBy === "lokasi") return lokasiHit;
        if (searchBy === "barang") return barangHit;
        return invoiceHit || klienHit || lokasiHit || barangHit;
      });
    }
    return [...list].sort((a, b) => {
      if (a.time !== b.time) return a.time < b.time ? -1 : 1;
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
  }, [dayTxns, q, searchBy, penjualDisplay, lokasiDisplay]);

  const totalNilai = dayTxns.reduce((s, t) => s + txnTotal(t), 0);
  const utangHariIni = dayTxns.reduce((s, t) => s + txnSisa(t), 0);

  const openCreate = () => setFormModal({ mode: "create", type: "PEMBELIAN", initial: null });
  // Pembelian batch 2: `type: t.type`, not a hardcoded "PEMBELIAN" — a TRANSFER row can now be
  // edited from here too. (Not a live bug either way: TransactionFormModal derives its own
  // internal txnType from `initial?.type` first, ahead of this `type` prop, whenever `initial`
  // is present — confirmed by direct read. Matched to Penjualan.js's own identical `openEdit`
  // for parity/defensiveness regardless.)
  const openEdit = (t) => setFormModal({ mode: "edit", type: t.type, initial: t });

  const submitForm = (draft) => {
    if (formModal?.mode === "edit") onUpdateTransaction(formModal.initial.id, draft);
    else onCreateTransaction(draft);
    setFormModal(null);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Pembelian</h1>
          <div className="page-sub">Transaksi pembelian per tanggal transaksi.</div>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Tambah Transaksi</button>
      </div>

      <DayNav date={selectedDate} onChange={setSelectedDate} />

      {/* Post-launch fix (Fix 3): built with the branch toggle on the SAME row as the primary
          toggle, pushed to the far right, from the start — mirrors Penjualan.js's own Fix 1. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "var(--gap-lg)", marginBottom: "var(--gap-lg)", flexWrap: "wrap" }}>
        <div className="seg">
          <button type="button" className={locFilter === "SEMUA" ? "active" : ""} onClick={() => setLocFilter("SEMUA")}>Semua</button>
          <button type="button" className={locFilter === "PUSAT" ? "active" : ""} onClick={() => setLocFilter("PUSAT")}>Pusat</button>
          <button type="button" className={locFilter === "CABANG" ? "active" : ""} onClick={() => setLocFilter("CABANG")}>Cabang</button>
        </div>

        {locFilter === "CABANG" && (
          <div className="seg">
            <button type="button" className={branchFilter === "SEMUA" ? "active" : ""} onClick={() => setBranchFilter("SEMUA")}>Semua Cabang</button>
            {branches.map((b) => (
              <button key={b.id} type="button" className={branchFilter === b.id ? "active" : ""} onClick={() => setBranchFilter(b.id)}>{b.name}</button>
            ))}
          </div>
        )}
      </div>

      <div className="daycards">
        <div className="daycard">
          <div className="daycard__label">Transaksi</div>
          <div className="daycard__value">{dayTxns.length}</div>
        </div>
        <div className="daycard">
          <div className="daycard__label">Total Nilai Pembelian</div>
          <div className="daycard__value">{fmtQty(totalNilai)}</div>
        </div>
        <div className="daycard daycard--piutang">
          <div className="daycard__label">Utang Hari Ini</div>
          <div className="daycard__value">{fmtQty(utangHariIni)}</div>
        </div>
      </div>

      <div className="toolbar">
        <select className="form-select search-by" value={searchBy} onChange={(e) => setSearchBy(e.target.value)}>
          <option value="semua">Semua</option>
          <option value="invoice">No. Invoice</option>
          <option value="klien">Penjual</option>
          <option value="lokasi">Lokasi</option>
          <option value="barang">Barang</option>
        </select>
        <input className="form-input grow" placeholder="Cari transaksi hari ini…"
          value={q} onChange={(e) => setQ(e.target.value)} />
        {/* Hide-internal filter (section F, batch 4) — same checkbox shape as Penjualan.js's own. */}
        <label style={{ display: "flex", alignItems: "center", gap: "var(--gap-sm)", whiteSpace: "nowrap", fontSize: 13 }}>
          <input type="checkbox" checked={hideInternal}
            onChange={(e) => setHideInternal(e.target.checked)} />
          Sembunyikan transfer internal
        </label>
      </div>

      {dayTxns.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>
          Belum ada transaksi pembelian pada tanggal ini.
        </div>
      ) : visible.length === 0 ? (
        <div className="empty-state">Tidak ada transaksi yang cocok dengan pencarian.</div>
      ) : (
        <div className="table-wrap">
          <table className="table txn-table txn-table--fixed">
            <colgroup>
              <col style={{ width: 95 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 100 }} />
              {/* Barang — dashboard alignment fix, round 3: see Penjualan.js's own identical
                  colgroup comment for the full real-measured recalibration (230px -> 215px). */}
              <col style={{ width: 215 }} />
              <col style={{ width: STOK_COL_WIDTH }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 130 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Tanggal &amp; Waktu</th>
                <th>No. Invoice</th>
                <th className="klien-cell">Penjual</th>
                <th className="lokasi-cell">Lokasi</th>
                <th>Barang</th>
                <th className="num">Stok</th>
                <th className="num">Subtotal Barang</th>
                <th className="num">Tagihan &amp; Potongan</th>
                <th className="num">Total Nilai</th>
                <th className="num">Total Setelah Biaya</th>
                <th className="status-cell">Status</th>
                <th className="due-cell">Jatuh Tempo</th>
                <th className="aksi-cell">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => {
                const isTransfer = t.type === "TRANSFER";
                const contact = contactsById.get(t.contactId);
                // Piutang & Utang batch 3 — closes the documented StatusPill inconsistency
                // (root CLAUDE.md's own "what is deliberately not built" entry): this page
                // shows a Transfer AS A PURCHASE BY THE DESTINATION, the opposite perspective
                // from Penjualan's own (which shows it as a sale by the SOURCE) — so the
                // perspective passed here is the destination's own branch, the flip of
                // Penjualan's `location === "PUSAT" ? null : t.branchId`. A Belum Lunas
                // transfer now reads "Utang" here, matching this same page's own "Utang Hari
                // Ini" day card, which already counted it as debt.
                const status = txnStatus(t, t.location === "PUSAT" ? t.branchId : null);
                const due = dueState(t, today());
                const lines = t.lines || [];
                // Pembelian batch 2 — perspective-flipped Lokasi/Penjual for a TRANSFER row (see
                // this file's own header comment): Lokasi = the DESTINATION (home, from this
                // page's own perspective — `lokasiDisplay`/`transferKlienName` already name the
                // real debtor, which IS the destination); Penjual = the SOURCE, read straight off
                // `t.location`/`t.branchId` (never a contact — a transfer's own contactId always
                // names the destination side's internal stand-in, the wrong side to show here).
                // Both now go through the SAME named helpers the search predicate above uses, so
                // the column and the search can never disagree about what a row "contains".
                const destBucket = t.location === "PUSAT" ? "CABANG" : "PUSAT";
                const lokasiLabel = lokasiDisplay(t);
                const penjualLabel = isTransfer ? resolveLocationLabel(t.location, t.branchId, branches) : (contact ? contact.name : "—");
                return (
                  <tr key={t.id} onClick={() => setDetailTxnId(t.id)} className={isTransfer ? "txn-row--transfer" : undefined}>
                    <td className="date-cell">{fmtDateShort(t.date)}<br /><span style={{ color: "var(--muted)", fontSize: 12 }}>{t.time}</span></td>
                    <td className="mono" style={{ color: isTransfer ? "var(--blue)" : "#555" }}>
                      {isTransfer ? (t.invoiceNumber || "—") : (t.supplierInvoiceNo || "—")}
                      {(t.costItems || []).length > 0 && <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>+Rincian</span>}
                    </td>
                    <td className="klien-cell">{penjualLabel}</td>
                    <td className="lokasi-cell">
                      <span className="chip chip--muted">{lokasiLabel}</span>
                      {isTransfer && <span className="chip chip--transfer" style={{ marginLeft: "var(--gap-sm)" }}>TRANSFER</span>}
                    </td>
                    <TxnBarangStokCell
                      lines={lines}
                      byCode={byCode}
                      stokWidth={STOK_COL_WIDTH}
                      renderStok={(l) =>
                        isTransfer ? (
                          <>
                            <span style={{ color: "var(--red)" }}>−{fmtQty(l.qty)} PCS {bucketLabel(t.location)}</span>
                            {" → "}
                            <span style={{ color: "var(--green)" }}>+{fmtQty(l.qty)} PCS {bucketLabel(destBucket)}</span>
                          </>
                        ) : (
                          <span style={{ color: "var(--green)" }}>+{fmtQty(l.qty)} PCS</span>
                        )
                      }
                    />
                    <SubtotalBarangCell txn={t} />
                    <RincianTambahanCell txn={t} />
                    <TxnNilaiCell txn={t} />
                    <TotalSetelahBiayaCell txn={t} perspective="PEMBELIAN" />
                    <td className="status-cell"><StatusPill status={status} /></td>
                    <td className="due-cell">{due.kind === "none" ? "—" : <DueBadge due={due} />}</td>
                    <td className="row-actions aksi-cell" onClick={(e) => e.stopPropagation()}>
                      <TxnRowActions
                        onEdit={() => openEdit(t)}
                        onBayarTagihan={status !== "LUNAS" ? () => setBayarTagihanTxnId(t.id) : undefined}
                        txnType={t.type}
                        onDelete={() => setDeleteTarget(t)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {formModal && (
        <TransactionFormModal
          mode={formModal.mode}
          type={formModal.type}
          initial={formModal.initial}
          items={items}
          contacts={contacts}
          salespeople={salespeople}
          persons={persons}
          branches={branches}
          siblingLinks={siblingLinks}
          transactions={transactions}
          defaultDueDays={defaultDueDays}
          onCreateContact={onCreateContact}
          onCreateContactFull={onCreateContactFull}
          onCreateSalespersonRole={onCreateSalespersonRole}
          onSubmit={submitForm}
          onClose={() => setFormModal(null)}
        />
      )}

      {detailTxn && (
        <TransactionDetailModal
          txn={detailTxn}
          items={items}
          contacts={contacts}
          salespeople={salespeople}
          persons={persons}
          branches={branches}
          today={today()}
          perspective="PEMBELIAN"
          onClose={() => setDetailTxnId(null)}
        />
      )}

      {bayarTagihanTxn && (
        <BayarTagihanModal
          txn={bayarTagihanTxn}
          contacts={contacts}
          branches={branches}
          perspective="PEMBELIAN"
          onRecordPayment={onRecordPayment}
          onClose={() => setBayarTagihanTxnId(null)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          title="Hapus transaksi?"
          message={<>
            Anda akan menghapus transaksi <b className="mono">{deleteTarget.supplierInvoiceNo || deleteTarget.invoiceNumber || deleteTarget.id}</b>.
            Stok yang sudah ditambahkan akan dikembalikan.<LinkedExpensesDeleteNote txnId={deleteTarget.id} /> Tindakan ini permanen.
          </>}
          requireText="HAPUS"
          onConfirm={() => {
            onDeleteTransaction(deleteTarget.id);
            if (detailTxnId === deleteTarget.id) setDetailTxnId(null);
            setDeleteTarget(null);
          }}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
